import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { MarkdownView } from '../components/MarkdownView';
import { useDateTimeFormat } from '../preferences/DateTimeFormatContext';
import { formatPrice } from '../lib/price';
import type { BazaarItem, SingleResponse } from '../api/types';

function sourceFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function BazaarDetail() {
  const { slugId } = useParams<{ slugId: string }>();
  const { t } = useTranslation();
  const { formatDateTime } = useDateTimeFormat();
  const [item, setItem] = useState<BazaarItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);

  useEffect(() => {
    if (!slugId) return;
    apiFetch<SingleResponse<BazaarItem>>(`/api/v1/bazaar-items/${slugId}`)
      .then((res) => setItem(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Alert severity="error">{error || t('bazaarDetail.notFound')}</Alert>
      </Container>
    );
  }

  const price = formatPrice(item.price_cents, item.currency);
  const source = sourceFromUrl(item.external_url);
  const slides = item.photo_urls.map((url) => ({ src: url }));

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('nav.bazaar'), to: '/bazaar' },
          { label: item.title },
        ]}
      />
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Box>
            <Typography variant="h3">{item.title}</Typography>
            {price && (
              <Typography variant="h4" color="primary" sx={{ mt: 1 }}>
                {price}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<OpenInNewIcon />}
            component="a"
            href={item.external_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('bazaarDetail.viewOriginalListing')}
          </Button>
        </Stack>

        {slides.length > 0 && (
          <>
            <Grid container spacing={2}>
              {slides.map((slide, idx) => (
                <Grid key={idx} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card>
                    <CardActionArea onClick={() => setLightboxIndex(idx)}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={slide.src}
                        alt={`${item.title} ${idx + 1}`}
                      />
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Lightbox
              open={lightboxIndex >= 0}
              close={() => setLightboxIndex(-1)}
              index={lightboxIndex >= 0 ? lightboxIndex : 0}
              slides={slides}
              plugins={[Zoom, Thumbnails]}
              zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
              thumbnails={{ position: 'bottom', width: 120, height: 80 }}
            />
          </>
        )}

        {item.description && (
          <>
            <Divider />
            <MarkdownView>{item.description}</MarkdownView>
          </>
        )}

        <Divider />

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('bazaarDetail.source')}: {source}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('bazaarDetail.addedOn')}: {formatDateTime(item.created_at)}
          </Typography>
        </Stack>
      </Stack>
    </Container>
  );
}
